// Every call the Create page makes to the backends, one function per route (plan step F14;
// promptchan-backend docs/architecture/create-page.md). Each sends what the page has always sent: the same
// host, method, headers and body. Each returns fetch's Response untouched, because the page branches on the
// status (300, 452, 479 and the rest) and reads the body itself.
//
// `token` is the person's Firebase ID token. The backends read it raw from Authorization, with no
// "Bearer". The headers differ from route to route only because they always have; making them uniform is a
// change of its own. When the shared client exists (plan step F4, src/api/client), these move there.

const CLOUD = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD
const FREE = process.env.NEXT_PUBLIC_PROMPTCHAN_FREE
const VIDEO = process.env.NEXT_PUBLIC_PROMPTCHAN_VIDEO

type Body = Record<string, unknown>

const jsonHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: token,
})

const postJson = (url: string, token: string, body: Body) =>
  fetch(url, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  })

// Image generation

/**
 * The backend an image request goes to: Free accounts and any request with an AI Character use the free
 * pool's host, paid accounts the main one. (Both are one backend since September 2026; the hosts still
 * differ.) A remembered job is collected from the same host (src/lib/image-jobs.js).
 */
export const imageBackend = ({
  paid,
  withCharacter,
}: {
  paid: boolean
  withCharacter: boolean
}) => (!paid || withCharacter ? FREE : CLOUD)

/** Generates images. Flame models have their own route on the same backend. */
export const generateImage = (
  apiBase: string | undefined,
  model: string,
  token: string,
  body: Body,
) =>
  postJson(
    `${apiBase}${model.includes('Flame') ? '/api/generate_image' : '/api/generate'}`,
    token,
    body,
  )

/** Edits an image with a text instruction (Qwen-Edit). */
export const tweakImage = (token: string, body: Body) =>
  postJson(`${CLOUD}/api/tweak_image`, token, body)

// Video, on the video machines (submit, status, result) and the main backend (the list, refunds)

export const submitVideo = (token: string, body: Body) =>
  postJson(`${VIDEO}/api/video_v3.1/submit`, token, body)

export const videoStatus = (token: string, requestId: string) =>
  fetch(`${VIDEO}/api/video_v3.1/status/${requestId}`, {
    headers: { Authorization: token },
  })

export const videoStatusWithLogs = (token: string, requestId: string) =>
  fetch(`${VIDEO}/api/video_v3.1/status_with_logs/${requestId}`, {
    headers: { Authorization: token },
  })

export const videoResult = (token: string, requestId: string) =>
  fetch(`${VIDEO}/api/video_v3.1/result/${requestId}`, {
    headers: { Authorization: token },
  })

/** The person's recent video jobs, for the history panel. */
export const listVideoJobs = (token: string) =>
  fetch(`${CLOUD}/api/video_v3.1/video_status`, {
    method: 'GET',
    headers: jsonHeaders(token),
  })

/** Asks for the gems of a failed video back. */
export const claimFailedVideo = (token: string, requestId: string) =>
  fetch(`${CLOUD}/api/video_v3.1/claim_failed/${requestId}`, {
    method: 'POST',
    headers: jsonHeaders(token),
  })

// Keeping results

export type PublishBody = {
  /** An image: its URL or base64. A video: its document id. */
  image: string
  folder: string | null
  visibility: 'Public' | 'Private'
}

export const publish = (
  token: string,
  kind: 'image' | 'video',
  body: PublishBody,
) =>
  postJson(
    `${CLOUD}/api/${kind === 'video' ? 'publish_video' : 'publish_image'}`,
    token,
    body,
  )

/** Deletes an image or a video: both are in the `images` collection. */
export const deleteMedia = (
  token: string,
  body: { imageId: string; imageUid: string },
) =>
  fetch(`${CLOUD}/api/delete_image`, {
    method: 'DELETE',
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  })

/** Saves a result's prompt and settings as a workflow (the Bookmark tab). */
export const savePrompt = (token: string, imageId: string) =>
  postJson(`${CLOUD}/api/save_prompt`, token, { image_id: imageId })

export const publicFolders = (token: string) =>
  fetch(`${CLOUD}/api/get_unique_folders`, {
    method: 'GET',
    headers: { Authorization: token },
  })

export const privateFolders = (token: string) =>
  fetch(`${CLOUD}/api/get_unique_folders_private`, {
    method: 'GET',
    headers: { Authorization: token },
  })

// Measurement: the caller never waits on these or shows their failure.

/** A thumb up or down on a result (backend myapp/output_feedback.py). */
export const sendOutputFeedback = (token: string, body: Body) =>
  postJson(`${CLOUD}/api/output_feedback`, token, body)

/** Someone tried to keep an early-access result (backend myapp/early_access.py). */
export const reportEarlyAccessBlocked = (token: string, body: Body) =>
  postJson(`${CLOUD}/api/early_access/blocked`, token, body)
